import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import type { DirectoryEntityType } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import type { SearchServiceSortOption } from '~/modules/search/type/api';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import { getDirectoryGridPageByOffset } from '@modules/category/util/get-directory-browse-data.server';
import type { AppLoadContext } from 'react-router';
import {
    hasEnoughPinnedSuppliers,
    interleaveByProfessional,
    isDefaultProductListEnabled,
    PRODUCTS_ROOT_PATH,
    stripTrailingSlash,
} from '@modules/category/util/default-product-list.server';
import { getFeaturedBrandPinsCached } from '@modules-rocco/professionals/api/get-featured-brand-pins.server';
import { getProductSuppliersPageByOffset } from '@modules/category/util/get-product-suppliers-page.server';
import { isProductSuppliersPage } from '~/modules/url/utils/product-suppliers';

interface ResolveDefaultProductListArgs {
    context: AppLoadContext;
    entityType: DirectoryEntityType;
    categoryLink: string;
    filters?: Record<string, unknown>;
}

/**
 * Returns the supplier filter to apply when this request is a further page of
 * the curated products root, or undefined when the ordinary catalogue applies.
 * Any user-applied filter hands the page back to the full catalogue, matching
 * the page loader's gate.
 */
const resolveDefaultProductList = async ({
    context,
    entityType,
    categoryLink,
    filters,
}: ResolveDefaultProductListArgs): Promise<
    { filters: Record<string, unknown> } | undefined
> => {
    if (
        entityType !== 'products' ||
        stripTrailingSlash(categoryLink) !== PRODUCTS_ROOT_PATH
    ) {
        return undefined;
    }

    if (filters && Object.keys(filters).length > 0) {
        return undefined;
    }

    if (!isDefaultProductListEnabled(context)) {
        return undefined;
    }

    const pinnedSuppliers = (await getFeaturedBrandPinsCached(context)).map(
        (brand) => brand.urlSegment,
    );

    return hasEnoughPinnedSuppliers(pinnedSuppliers)
        ? { filters: { professionals: pinnedSuppliers } }
        : undefined;
};

/** Resource-route path for the directory browse View More (all entity types). */
export const LOAD_MORE_DIRECTORY_ACTION = '/actions/load-more-directory';

/** Max tiles per request — guards against manipulated `limit` values. */
const MAX_LIMIT = 100;

const ENTITY_TYPES: DirectoryEntityType[] = [
    'products',
    'professionals',
    'projects',
];

const parseEntityType = (value: string | null): DirectoryEntityType =>
    ENTITY_TYPES.includes(value as DirectoryEntityType)
        ? (value as DirectoryEntityType)
        : 'products';

/** `/products`, `/professionals`, `/projects` roots per entity. */
const entityBase = (entityType: DirectoryEntityType): string =>
    `/${entityType}`;

/**
 * Validate the `sort` param to a Search Service sort option. Filter-style sorts
 * (`uncategorised` / `invalidLink`) aren't valid grid orderings, so they resolve
 * to undefined and the caller falls back to the entity default.
 */
const parseSort = (
    value: string | null,
): SearchServiceSortOption | undefined => {
    const sort = validateSort(value ?? undefined);
    if (!sort || sort === 'uncategorised' || sort === 'invalidLink') {
        return undefined;
    }
    return sort;
};

/**
 * Fetch the next page of the flat directory browse grid (offset/limit + filters)
 * for the View More hook. Entity-agnostic: dispatches to the right search index
 * by `entityType`.
 *
 * Query params: `entityType`, `categoryLink`, `offset`, `limit`, `filters` (JSON).
 * Security: validates `categoryLink` is a same-directory path (no traversal).
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);

    const entityType = parseEntityType(url.searchParams.get('entityType'));
    const categoryLink = url.searchParams.get('categoryLink') ?? '';
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');
    const filtersParam = url.searchParams.get('filters');
    // The in-listing keyword term, repeated so later pages stay inside the
    // same result set the first page came from.
    const search = url.searchParams.get('search')?.trim() || undefined;
    const sortBy = parseSort(url.searchParams.get('sort'));

    const base = entityBase(entityType);
    const categoryLinkPattern = new RegExp(`^${base}(/[\\w-]+)*$`);

    if (
        !categoryLink.startsWith(base) ||
        categoryLink.includes('..') ||
        !categoryLinkPattern.test(categoryLink)
    ) {
        logger.warning('Invalid categoryLink in load-more-directory', {
            entityType,
            categoryLink,
        });
        return { tiles: [], hasMore: false, totalCount: 0 };
    }

    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    const limit = limitParam ? parseInt(limitParam, 10) : 36;
    if (
        !Number.isInteger(offset) ||
        offset < 0 ||
        !Number.isInteger(limit) ||
        limit < 1 ||
        limit > MAX_LIMIT
    ) {
        logger.warning('Invalid offset/limit in load-more-directory', {
            offsetParam,
            limitParam,
        });
        return { tiles: [], hasMore: false, totalCount: 0 };
    }

    let filters: Record<string, unknown> | undefined;
    if (filtersParam) {
        try {
            const parsed = JSON.parse(filtersParam);
            if (typeof parsed === 'object' && parsed !== null) {
                filters = parsed;
            }
        } catch {
            logger.warning('Invalid filters JSON in load-more-directory', {
                filtersParam,
            });
        }
    }

    // "Browse by Brands" (/products/suppliers) is a view over the product
    // index, not an entity type of its own — the `/suppliers` segment in the
    // link is what selects it. It needs the aggregate-then-search pass rather
    // than a single index read.
    const isSuppliers =
        entityType === 'products' && isProductSuppliersPage(categoryLink);
    if (isSuppliers) {
        try {
            const { tiles, hasMore, totalCount } =
                await getProductSuppliersPageByOffset({
                    context,
                    categoryLink,
                    offset,
                    limit,
                    filters,
                    search,
                    sortBy,
                    signal: request.signal,
                });
            return {
                tiles: tiles.map((model) => ({ model })),
                hasMore,
                totalCount,
            };
        } catch (error) {
            logger.error('Error loading more product suppliers', {
                error,
                categoryLink,
                offset,
                limit,
            });
            return routerData(
                { tiles: [], hasMore: false, totalCount: 0 },
                { status: 500 },
            );
        }
    }

    // The curated products root narrows and reorders the grid server-side, so
    // later pages have to repeat that work — the client sends no filters for it
    // (deliberately, so nothing shows up as an applied filter in the UI).
    const curated = await resolveDefaultProductList({
        context,
        entityType,
        categoryLink,
        filters,
    });

    try {
        const { tiles, hasMore, totalCount } =
            await getDirectoryGridPageByOffset({
                context,
                entityType,
                categoryLink,
                offset,
                limit,
                filters: curated?.filters ?? filters,
                search,
                sortBy,
                hitsTransform: curated ? interleaveByProfessional : undefined,
                signal: request.signal,
            });

        // Wrap raw models as tile view models; the caller stamps the aspect ratio.
        return {
            tiles: tiles.map((model) => ({ model })),
            hasMore,
            totalCount,
        };
    } catch (error) {
        logger.error('Error in /actions/load-more-directory', {
            error,
            entityType,
            categoryLink,
            offset,
            limit,
        });
        return routerData(
            { tiles: [], hasMore: false, totalCount: 0 },
            { status: 500 },
        );
    }
};

/** Never revalidate — this endpoint only serves explicit `fetcher.load` calls. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
